import React, { useState, useEffect } from 'react';
import { 
    getRoadmapSettings, 
    updateRoadmapSettings,
    getIncomeTypeSettings,
    updateIncomeTypeSettings,
    getCoupleSettings,
    updateCoupleSettings
} from '../../../services/roadmap.service';
import Swal from 'sweetalert2';

const RoadmapSettings = () => {
    const [isEnabled, setIsEnabled] = useState(true);
    const [isPartnershipEnabled, setIsPartnershipEnabled] = useState(true);
    const [isCoupleEnabled, setIsCoupleEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const [roadmapSet, incomeSet, coupleSet] = await Promise.all([
                getRoadmapSettings(),
                getIncomeTypeSettings(),
                getCoupleSettings()
            ]);
            setIsEnabled(roadmapSet.enabled);
            setIsPartnershipEnabled(incomeSet.partnershipEnabled);
            setIsCoupleEnabled(coupleSet.coupleEnabled);
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async () => {
        const newValue = !isEnabled;
        setSaving(true);
        try {
            await updateRoadmapSettings(newValue);
            setIsEnabled(newValue);
            Swal.fire({
                title: 'Success!',
                text: `Roadmap module has been turned ${newValue ? 'ON' : 'OFF'}.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to update settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handlePartnershipToggle = async () => {
        const newValue = !isPartnershipEnabled;
        setSaving(true);
        try {
            await updateIncomeTypeSettings(newValue);
            setIsPartnershipEnabled(newValue);
            Swal.fire({
                title: 'Success!',
                text: `Partnership Business option has been turned ${newValue ? 'ON' : 'OFF'}.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to update settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCoupleToggle = async () => {
        const newValue = !isCoupleEnabled;
        setSaving(true);
        try {
            await updateCoupleSettings(newValue);
            setIsCoupleEnabled(newValue);
            Swal.fire({
                title: 'Success!',
                text: `Couple income type has been turned ${newValue ? 'ON' : 'OFF'}.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to update couple settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-loading">Loading settings...</div>;

    return (
        <div className="admin-content-v2">
            <div className="admin-header-v2">
                <div>
                    <h1 className="admin-title-v2">Feature Control Settings</h1>
                    <p className="admin-subtitle-v2">Manage modules and form options across the platform</p>
                </div>
            </div>

            <div className="settings-card-v2">

                {/* Toggle 1: Roadmap Module */}
                <div className="setting-item-v2">
                    <div className="setting-info-v2">
                        <h3>Enable Roadmap Module</h3>
                        <p>Turn this off to hide the "Build Roadmap" button from the user's report landing page.</p>
                    </div>
                    <div className="setting-action-v2">
                        <label className="switch-v2">
                            <input 
                                type="checkbox" 
                                checked={isEnabled} 
                                onChange={handleToggle}
                                disabled={saving}
                            />
                            <span className="slider-v2 round"></span>
                        </label>
                        <span className={`status-badge-v2 ${isEnabled ? 'status-active' : 'status-inactive'}`}>
                            {isEnabled ? 'ON' : 'OFF'}
                        </span>
                    </div>
                </div>

                <div className="setting-divider"></div>

                {/* Toggle 2: Partnership Business */}
                <div className="setting-item-v2">
                    <div className="setting-info-v2">
                        <h3>Business (Partnership/LLP/Pvt Ltd) Option</h3>
                        <p>Toggle visibility of the Partnership/LLP business type in the registration form.</p>
                    </div>
                    <div className="setting-action-v2">
                        <label className="switch-v2">
                            <input 
                                type="checkbox" 
                                checked={isPartnershipEnabled} 
                                onChange={handlePartnershipToggle}
                                disabled={saving}
                            />
                            <span className="slider-v2 round"></span>
                        </label>
                        <span className={`status-badge-v2 ${isPartnershipEnabled ? 'status-active' : 'status-inactive'}`}>
                            {isPartnershipEnabled ? 'ON' : 'OFF'}
                        </span>
                    </div>
                </div>

                <div className="setting-divider"></div>

                {/* Toggle 3: Couple Income Type */}
                <div className="setting-item-v2">
                    <div className="setting-info-v2">
                        <h3>Couple Income Type (Joint Salary)</h3>
                        <p>
                            Turn this <strong>OFF</strong> to hide the "Couple" option from the Single/Couple
                            dropdown in the Income Type section of the diagnosis form.
                            When disabled, users can only select <em>Single</em>.
                        </p>
                    </div>
                    <div className="setting-action-v2">
                        <label className="switch-v2">
                            <input 
                                type="checkbox" 
                                checked={isCoupleEnabled} 
                                onChange={handleCoupleToggle}
                                disabled={saving}
                            />
                            <span className="slider-v2 round"></span>
                        </label>
                        <span className={`status-badge-v2 ${isCoupleEnabled ? 'status-active' : 'status-inactive'}`}>
                            {isCoupleEnabled ? 'ON' : 'OFF'}
                        </span>
                    </div>
                </div>

            </div>

            <style>{`
                .admin-content-v2 { padding: 2rem; }
                .settings-card-v2 { 
                    background: white; 
                    padding: 2rem; 
                    border-radius: 12px; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                    max-width: 800px;
                }
                .setting-divider {
                    height: 1px;
                    background: #f1f5f9;
                    margin: 2rem 0;
                }
                .setting-item-v2 {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 2rem;
                }
                .setting-info-v2 h3 { margin: 0 0 0.5rem 0; color: #1e293b; }
                .setting-info-v2 p { margin: 0; color: #64748b; font-size: 0.9rem; line-height: 1.5; }
                .setting-info-v2 strong { color: #1e293b; }
                .setting-info-v2 em { color: #475569; font-style: normal; font-weight: 500; }
                
                .setting-action-v2 {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.75rem;
                    flex-shrink: 0;
                }

                /* Toggle Switch */
                .switch-v2 {
                    position: relative;
                    display: inline-block;
                    width: 60px;
                    height: 34px;
                }
                .switch-v2 input { opacity: 0; width: 0; height: 0; }
                .slider-v2 {
                    position: absolute;
                    cursor: pointer;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: #ccc;
                    transition: .4s;
                }
                .slider-v2:before {
                    position: absolute;
                    content: "";
                    height: 26px; width: 26px;
                    left: 4px; bottom: 4px;
                    background-color: white;
                    transition: .4s;
                }
                input:checked + .slider-v2 { background-color: #069668; }
                input:focus + .slider-v2 { box-shadow: 0 0 1px #069668; }
                input:checked + .slider-v2:before { transform: translateX(26px); }
                .slider-v2.round { border-radius: 34px; }
                .slider-v2.round:before { border-radius: 50%; }

                .status-badge-v2 {
                    font-size: 0.75rem;
                    font-weight: 800;
                    padding: 4px 12px;
                    border-radius: 100px;
                    text-transform: uppercase;
                }
                .status-active { background: #dcfce7; color: #166534; }
                .status-inactive { background: #fee2e2; color: #991b1b; }
            `}</style>
        </div>
    );
};

export default RoadmapSettings;
